import {
  Pricing_Read,
  PricingEvent_Read,
  PricingEventRuleConditionType,
  RateType,
} from '@treadinc/horizon-api-spec';
import dayjs, { Dayjs } from 'dayjs';

/**
 * Model classes for the load-level pricing feature (`load_level_pricing` flag): the `Pricing`
 * snapshot embedded on Load reads and the `PricingEvent` audit records behind the Rates History
 * timelines.
 *
 * Raw shapes come from `@treadinc/horizon-api-spec` (`Pricing_Read`, `PricingEvent_Read`). Where
 * the published spec is looser or incomplete than the live API, `parse` narrows to the FE
 * refinement types below — each is annotated with the gap it covers and should be dropped once
 * the spec catches up.
 */

/**
 * Spec gap: `Pricing_Read.*_rate.resolution` is typed as a bare `string`. These are the values the
 * backend emits; the union drives the resolution chip colors/labels.
 */
export type PricingResolution = 'inherited' | 'matched' | 'manual' | 'frozen';

/**
 * Spec gap: `PricingEvent_Read.action` is typed as a bare `string`. These are the action values
 * the backend writes (see `PricingEventService::Create`); the union drives the timeline labels and
 * dot colors.
 */
export type PricingEventAction =
  | 'srs_match'
  | 'cascade'
  | 'manual_edit'
  | 'load_divergence'
  | 'freeze'
  | 'assignment'
  | 'backfill'
  // Emitted when a write is rejected because the dimension is locked by invoice approval
  // (TRE-15986). Rendered as "blocked" — the enum value stays `refused` to match the backend.
  | 'refused';

/** One add-on as serialized into a Pricing snapshot (the add-on dims' change values are arrays). */
export interface AddOnSnapshot {
  name: string;
  [key: string]: unknown;
}

// The three per-side rate snapshots (`customer_rate` / `vendor_rate` / `driver_rate`) share one
// inline shape in the spec.
type PricingRateRead = Pricing_Read['customer_rate'];

export class PricingRate {
  constructor(
    private _value: number | null,
    private _type: RateType | null,
    private _resolution: PricingResolution | null,
    private _rate_id: string | null,
  ) {}

  public static parse(proto: PricingRateRead) {
    return new PricingRate(
      proto.value === null || proto.value === undefined ? null : Number(proto.value),
      // Spec gap: `type` and `resolution` are bare strings; the backend emits exactly the
      // RateType / PricingResolution values.
      (proto.type ?? null) as RateType | null,
      (proto.resolution ?? null) as PricingResolution | null,
      proto.rate_id ?? null,
    );
  }

  public get value() {
    return this._value;
  }

  public get type() {
    return this._type;
  }

  public get resolution() {
    return this._resolution;
  }

  public get rateId() {
    return this._rate_id;
  }
}

/**
 * One immutable pricing snapshot, as embedded on Load reads (`pricing` / `driver_pricing`) when
 * `load_level_pricing` is ON. Maps the snapshot identity plus the three per-side rates the FE
 * consumes; the add-on / material / prevailing-wage / tax groups of `Pricing_Read` are deliberately
 * unmapped until a consumer arrives.
 */
export class Pricing {
  constructor(
    private _id: string,
    private _customer_rate: PricingRate,
    private _vendor_rate: PricingRate,
    private _driver_rate: PricingRate,
  ) {}

  public static parse(proto: Pricing_Read) {
    return new Pricing(
      proto.id,
      PricingRate.parse(proto.customer_rate),
      PricingRate.parse(proto.vendor_rate),
      PricingRate.parse(proto.driver_rate),
    );
  }

  public get id() {
    return this._id;
  }

  public get customerRate() {
    return this._customer_rate;
  }

  public get vendorRate() {
    return this._vendor_rate;
  }

  public get driverRate() {
    return this._driver_rate;
  }
}

/**
 * Heterogeneous by dimension — a scalar for rates/resolutions/flags, an array of add-ons for the
 * `*_add_ons` dims, null when cleared.
 */
export type PricingEventChangeValue = string | number | boolean | AddOnSnapshot[] | null;

// Spec gap: `PricingEvent_Read.changes` elements are typed `{ [key: string]: unknown }`. The
// backend serializes one per-dimension before/after diff (`pricing_event.object_changes`).
type PricingEventChangeRead = NonNullable<PricingEvent_Read['changes']>[number];

export class PricingEventChange {
  constructor(
    private _dim: string,
    private _from: PricingEventChangeValue,
    private _to: PricingEventChangeValue,
  ) {}

  public static parse(proto: PricingEventChangeRead) {
    return new PricingEventChange(
      proto.dim as string,
      (proto.from ?? null) as PricingEventChangeValue,
      (proto.to ?? null) as PricingEventChangeValue,
    );
  }

  public get dim() {
    return this._dim;
  }

  public get from() {
    return this._from;
  }

  public get to() {
    return this._to;
  }
}

type PricingEventRuleConditionRead = NonNullable<
  PricingEvent_Read['rule']
>['conditions'][number];

/** One match condition on the rate card that drove a pricing event, resolved to its name. */
export class PricingEventRuleCondition {
  constructor(
    private _type: PricingEventRuleConditionType,
    private _label: string,
  ) {}

  public static parse(proto: PricingEventRuleConditionRead) {
    return new PricingEventRuleCondition(proto.type, proto.label);
  }

  public get type() {
    return this._type;
  }

  public get label() {
    return this._label;
  }
}

type PricingEventActorRead = NonNullable<PricingEvent_Read['actor']>;

class PricingEventActor {
  constructor(
    private _id: string,
    private _type: string,
    private _name: string | null,
  ) {}

  public static parse(proto: PricingEventActorRead) {
    return new PricingEventActor(proto.id, proto.type, proto.name);
  }

  public get id() {
    return this._id;
  }

  public get type() {
    return this._type;
  }

  public get name() {
    return this._name;
  }
}

/**
 * One audit record from `GET /v1/pricing_events` — the Rates History timelines are built from
 * these. One atomic change writes several records sharing an `actionId` (fanned out per grain and,
 * on counterparty orders, split by audience); see `mergeEventsByActionId` in
 * `~components/Job/LoadPricing/pricingEventsHelpers`.
 */
export class PricingEvent {
  constructor(
    private _id: string,
    private _action: PricingEventAction,
    private _action_id: string,
    private _subject_type: string,
    private _subject_id: string,
    private _summary: string | null,
    private _occurred_at: Dayjs,
    private _pricing_id: string,
    private _order_id: string,
    private _changes: PricingEventChange[] | null,
    private _actor: PricingEventActor | null,
    /**
     * Name of the rate card that drove this change, snapshotted at write time. Only rule-driven
     * call sites stamp it (TRE-15450), so its ABSENCE is meaningful: it distinguishes a rate-card
     * edit from the order's own tax/material change, both of which arrive as `cascade`.
     */
    private _rule_name: string | null = null,
    /**
     * The card's match conditions — why it applied. Empty for a catch-all card and for an event no
     * card drove; `ruleName` already carries that distinction, so this stays an array either way.
     * Resolved at read time from the card's CURRENT config, unlike the write-time `ruleName`
     * snapshot — editing a card rewrites the conditions shown against past events.
     */
    private _rule_conditions: PricingEventRuleCondition[] = [],
    /** `TRD-#####` of the subject's job. Null for an order-grain event, which has no single job. */
    private _subject_job_ref: string | null = null,
  ) {}

  public static parse(proto: PricingEvent_Read) {
    return new PricingEvent(
      proto.id,
      // Spec gap: `action` is a bare string; the backend writes exactly the
      // PricingEventAction values.
      proto.action as PricingEventAction,
      proto.action_id,
      proto.subject_type,
      proto.subject_id,
      proto.summary,
      dayjs(proto.occurred_at),
      proto.pricing_id,
      proto.order_id,
      proto.changes ? proto.changes.map(PricingEventChange.parse) : null,
      proto.actor ? PricingEventActor.parse(proto.actor) : null,
      // Both are OMITTED rather than nulled when absent, so `?.` is doing real work here.
      proto.rule?.name ?? null,
      proto.rule?.conditions.map(PricingEventRuleCondition.parse) ?? [],
      proto.subject_context?.job_ref ?? null,
    );
  }

  /**
   * Copy representing one merged timeline entry, keeping the cached originals untouched. THIS
   * record supplies the entry's identity — its grain (`subjectType`), action, summary and job ref —
   * while `changes`, `occurredAt`, `actor` and the rule are resolved across every record of the
   * action, so no part of the entry depends on the order the API returned rows in. See
   * `mergeEventsByActionId`.
   */
  public asMergedEntry({
    actor,
    changes,
    occurredAt,
    ruleConditions,
    ruleName,
  }: {
    actor: PricingEvent['actor'];
    changes: PricingEventChange[] | null;
    occurredAt: Dayjs;
    ruleConditions: PricingEventRuleCondition[];
    ruleName: string | null;
  }) {
    return new PricingEvent(
      this._id,
      this._action,
      this._action_id,
      this._subject_type,
      this._subject_id,
      this._summary,
      occurredAt,
      this._pricing_id,
      this._order_id,
      changes,
      actor,
      ruleName,
      ruleConditions,
      this._subject_job_ref,
    );
  }

  public get id() {
    return this._id;
  }

  public get action() {
    return this._action;
  }

  public get actionId() {
    return this._action_id;
  }

  public get subjectType() {
    return this._subject_type;
  }

  public get subjectId() {
    return this._subject_id;
  }

  public get summary() {
    return this._summary;
  }

  public get occurredAt() {
    return this._occurred_at;
  }

  public get pricingId() {
    return this._pricing_id;
  }

  public get orderId() {
    return this._order_id;
  }

  public get changes() {
    return this._changes;
  }

  public get actor() {
    return this._actor;
  }

  public get ruleName() {
    return this._rule_name;
  }

  public get ruleConditions() {
    return this._rule_conditions;
  }

  public get subjectJobRef() {
    return this._subject_job_ref;
  }
}
